import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
    ACCOUNT_TYPES,
    ACCOUNT_TYPE_LABELS,
    ACCOUNT_TYPE_BADGES,
    HOME_MAP_REVERSE,
    DEFAULT_ACCOUNTS
} from '../../constants';
import { generateAccountId } from '../../constants/defaultAccounts';
import { toHalfWidth } from '../../utils/formatting';

const AccountMaster = ({ accounts, setAccounts, showAlert }) => {
    const [showModal, setShowModal] = useState(false);
    const [editingAccount, setEditingAccount] = useState(null);
    const [createdName, setCreatedName] = useState(null); // 作成完了モーダル用
    const kanaRef = useRef(null);
    useEffect(() => {
        if (kanaRef.current) {
            kanaRef.current.value = formData.kana;
        }
    }, [showModal, editingAccount]);
    const defaultFormData = {
        code: '',
        name: '',
        kana: '',
        type: ACCOUNT_TYPES.ASSET,
        home: 'debit',
        statement: 'BS',
        counterpart: '',
        description: ''
    };
    const [formData, setFormData] = useState(defaultFormData);
    const handleSubmit = async () => {
        if (!formData.code || !formData.name) {
            showAlert('コードと名称は必須です', 'error');
            return;
        }

        // IMEの影響を排除するため、提出時にDOMから直接kanaを読んでクリーニング
        const finalKana = kanaRef.current
            ? toHalfWidth(kanaRef.current.value).replace(/[^a-zA-Z]/g, '').toUpperCase()
            : formData.kana;

        if (editingAccount) {
            // コード変更時、他の科目と重複チェック
            if (formData.code !== editingAccount.code && accounts.some(a => a.code === formData.code && a.id !== editingAccount.id)) {
                showAlert('このコードは既に使用されています', 'error');
                return;
            }
            const ok = await setAccounts(accounts.map(a =>
                a.id === editingAccount.id
                    ? { ...a, ...formData, kana: finalKana, id: editingAccount.id } // IDは不変
                    : a
            ));
            if (ok !== false) {
                showAlert('勘定科目を更新しました');
                setShowModal(false);
                setEditingAccount(null);
                setFormData(defaultFormData);
            }
        } else {
            if (accounts.some(a => a.code === formData.code)) {
                showAlert('このコードは既に使用されています', 'error');
                return;
            }
            // 新規追加時は統一フォーマットでID生成: acc_{code}_{name}
            const newAccount = { ...formData, kana: finalKana, id: generateAccountId(formData.code, formData.name) };
            const savedName = formData.name;
            const ok = await setAccounts([...accounts, newAccount]);
            if (ok !== false) {
                setShowModal(false);
                setEditingAccount(null);
                setCreatedName(savedName);
                setFormData(defaultFormData);
            }
        }
    };

    const openNewWithCode = (code) => {
        setEditingAccount(null);
        setFormData({ ...defaultFormData, code: String(code) });
        setShowModal(true);
    };

    const handleEdit = (account) => {
        setEditingAccount(account);
        setFormData({
            code: account.code,
            name: account.name,
            kana: account.kana || '',
            type: account.type,
            home: account.home || 'debit',
            statement: account.statement || 'BS',
            counterpart: account.counterpart || '',
            description: account.description || ''
        });
        setShowModal(true);
    };

    const handleDelete = async (id) => {
        if (confirm('この勘定科目を削除してよろしいですか？')) {
            const ok = await setAccounts(accounts.filter(a => a.id !== id));
            if (ok !== false) showAlert('勘定科目を削除しました');
        }
    };

    const resetToDefault = async () => {
        if (confirm('勘定科目をデフォルトに戻しますか？既存の仕訳データには影響しません。')) {
            const ok = await setAccounts(DEFAULT_ACCOUNTS);
            if (ok !== false) showAlert('勘定科目をデフォルトに戻しました');
        }
    };

    const accountsByType = useMemo(() => {
        const grouped = {};
        Object.values(ACCOUNT_TYPES).forEach(type => {
            grouped[type] = accounts
                .filter(a => a.type === type)
                .sort((a, b) => {
                    // 数値として比較（コードが数値の場合）
                    const aNum = parseInt(a.code);
                    const bNum = parseInt(b.code);
                    if (!isNaN(aNum) && !isNaN(bNum)) {
                        return aNum - bNum;
                    }
                    // 数値でない場合は文字列比較
                    return a.code.localeCompare(b.code);
                });
        });
        return grouped;
    }, [accounts]);

    return (
        <div>
            <div className="page-header">
                <h2>勘定科目マスタ</h2>
            </div>

            <div className="card">
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px'}}>
                    <h3 className="card-title" style={{marginBottom: 0, border: 'none', paddingBottom: 0}}>勘定科目一覧</h3>
                    <div className="btn-group" style={{marginTop: 0}}>
                        <button className="btn btn-primary" onClick={() => setShowModal(true)}>
                            新規追加
                        </button>
                        <button className="btn btn-secondary" onClick={resetToDefault}>
                            デフォルトに戻す
                        </button>
                    </div>
                </div>

                {Object.entries(accountsByType).map(([type, accs]) => (
                    <div key={type} style={{marginBottom: '30px'}}>
                        <h4 style={{marginBottom: '10px'}}>
                            <span className={`badge ${ACCOUNT_TYPE_BADGES[type]}`}>
                                {ACCOUNT_TYPE_LABELS[type]}
                            </span>
                        </h4>
                        <table>
                            <thead>
                                <tr>
                                    <th style={{width: '80px'}}>コード</th>
                                    <th>名称</th>
                                    <th style={{width: '100px'}}>読み</th>
                                    <th style={{width: '60px'}}>ホーム</th>
                                    <th style={{width: '120px'}}>相方</th>
                                    <th style={{width: '150px'}}>解説</th>
                                    <th style={{width: '120px'}}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {accs.map((account, idx) => {
                                    const currentCode = parseInt(account.code);
                                    const nextCode = accs[idx + 1] ? parseInt(accs[idx + 1].code) : null;
                                    const hasGap = nextCode !== null && !isNaN(currentCode) && !isNaN(nextCode) && nextCode > currentCode + 1;
                                    return (
                                        <React.Fragment key={account.id}>
                                            <tr title={account.description || ''}>
                                                <td>{account.code}</td>
                                                <td>{account.name}</td>
                                                <td>{account.kana || ''}</td>
                                                <td className="text-center">{HOME_MAP_REVERSE[account.home] || ''}</td>
                                                <td>{account.counterpart || ''}</td>
                                                <td style={{fontSize: '12px', color: '#666'}}>{account.description || ''}</td>
                                                <td>
                                                    <div className="action-buttons">
                                                        <button className="btn btn-secondary btn-icon" onClick={() => handleEdit(account)}>編集</button>
                                                        <button className="btn btn-danger btn-icon" onClick={() => handleDelete(account.id)}>削除</button>
                                                    </div>
                                                </td>
                                            </tr>
                                            {hasGap && (
                                                <tr>
                                                    <td colSpan={7} style={{padding: '3px 8px', background: '#f5f5f5'}}>
                                                        <button
                                                            className="btn btn-secondary"
                                                            style={{fontSize: '12px', padding: '2px 10px', opacity: 0.7}}
                                                            onClick={() => openNewWithCode(currentCode + 1)}
                                                        >
                                                            ＋ 新規追加（コード {currentCode + 1}〜）
                                                        </button>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                ))}
            </div>

            {/* モーダル */}
            {showModal && (
                <div className="modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="modal" onClick={e => e.stopPropagation()}>
                        <h3>{editingAccount ? '勘定科目を編集' : '新規勘定科目'}</h3>

                        <div className="form-group">
                            <label>コード</label>
                            <input
                                type="text"
                                value={formData.code}
                                onChange={e => setFormData({...formData, code: e.target.value})}
                                placeholder="例: 101"
                                style={{ width: '200px' }}
                            />
                        </div>

                        <div className="form-group">
                            <label>名称</label>
                            <input
                                type="text"
                                value={formData.name}
                                onChange={e => setFormData({...formData, name: e.target.value})}
                                placeholder="例: 現金"
                                style={{ width: '100%', maxWidth: '500px' }}
                            />
                        </div>

                        <div className="form-group">
                            <label>読み（カナ）</label>
                            <input
                                ref={kanaRef}
                                type="text"
                                defaultValue=""
                                onBlur={(e) => {
                                    // フォーカスを外したタイミングで半角大文字英字に変換
                                    const cleaned = toHalfWidth(e.target.value).replace(/[^a-zA-Z]/g, '').toUpperCase();
                                    e.target.value = cleaned;
                                    setFormData(prev => ({...prev, kana: cleaned}));
                                }}
                                placeholder="例: GENKIN"
                                style={{ width: '100%', maxWidth: '400px' }}
                            />
                            <p style={{fontSize: '0.8em', color: 'gray', margin: '4px 0 0'}}>
                                半角大文字英字。フォーカスを外すと自動変換されます
                            </p>
                        </div>

                        <div className="form-group">
                            <label>大区分</label>
                            <select
                                value={formData.type}
                                onChange={e => setFormData({...formData, type: e.target.value})}
                            >
                                {Object.entries(ACCOUNT_TYPE_LABELS).map(([key, label]) => (
                                    <option key={key} value={key}>{label}</option>
                                ))}
                            </select>
                        </div>

                        <div className="form-group">
                            <label>ホーム</label>
                            <select
                                value={formData.home}
                                onChange={e => setFormData({...formData, home: e.target.value})}
                            >
                                <option value="debit">借方</option>
                                <option value="credit">貸方</option>
                            </select>
                        </div>

                        <div className="form-group">
                            <label>相方（デフォルト相手科目）</label>
                            <input
                                type="text"
                                value={formData.counterpart}
                                onChange={e => setFormData({...formData, counterpart: e.target.value})}
                                placeholder="例: 普通預金"
                                style={{ width: '100%', maxWidth: '400px' }}
                            />
                        </div>

                        <div className="form-group">
                            <label>解説</label>
                            <textarea
                                value={formData.description}
                                onChange={e => setFormData({...formData, description: e.target.value})}
                                placeholder="例: 手許現金"
                                style={{ width: '100%', minHeight: '80px', resize: 'vertical' }}
                                rows={3}
                            />
                        </div>

                        <div className="btn-group">
                            <button className="btn btn-primary" onClick={handleSubmit}>
                                {editingAccount ? '更新' : '追加'}
                            </button>
                            <button
                                className="btn btn-secondary"
                                onClick={() => {
                                    setShowModal(false);
                                    setEditingAccount(null);
                                    setFormData(defaultFormData);
                                }}
                            >
                                キャンセル
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 作成完了モーダル */}
            {createdName !== null && (
                <div className="modal-overlay" onClick={() => { setCreatedName(null); setShowModal(false); }}>
                    <div className="modal" onClick={e => e.stopPropagation()} style={{textAlign: 'center', maxWidth: '360px'}}>
                        <p style={{fontSize: '18px', marginBottom: '24px'}}>「{createdName}」を作りました</p>
                        <div className="btn-group" style={{justifyContent: 'center'}}>
                            <button
                                className="btn btn-primary"
                                onClick={() => {
                                    setCreatedName(null);
                                    setShowModal(true);
                                }}
                            >
                                続けて登録
                            </button>
                            <button
                                className="btn btn-secondary"
                                onClick={() => { setCreatedName(null); setShowModal(false); }}
                            >
                                終了
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AccountMaster;
